import { ThemeOptions } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { CSSProperties } from 'react';

import { fontWeight } from './constants/fonts';

declare module '@mui/material/styles' {
  interface TypographyVariantsOptions {
    fontWeightSemiBold?: number;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    headlineH1: CSSProperties;
    headlineH2: CSSProperties;
    headlineH3: CSSProperties;
    headlineH4: CSSProperties;
    headlineH5: CSSProperties;
    headlineH6: CSSProperties;
    bodyB1Regular: CSSProperties;
    bodyB1Medium: CSSProperties;
    bodyB1Strong: CSSProperties;
    bodyB2Regular: CSSProperties;
    bodyB2Medium: CSSProperties;
    bodyB2Strong: CSSProperties;
    body1Bold: CSSProperties;
    body1Medium: CSSProperties;
    body2Bold: CSSProperties;
    body2Medium: CSSProperties;
    captionBold: CSSProperties;
    caption2: CSSProperties;
    caption2Bold: CSSProperties;
    captionC1Regular: CSSProperties;
    captionC1Strong: CSSProperties;
    captionC2Strong: CSSProperties;
    paragraphP1: CSSProperties;
    paragraphP2: CSSProperties;
    paragraphP3: CSSProperties;
    paragraphP3Strong: CSSProperties;
  }

  interface TypographyVariantsOptions {
    headlineH1: CSSProperties;
    headlineH2: CSSProperties;
    headlineH3: CSSProperties;
    headlineH4: CSSProperties;
    headlineH5: CSSProperties;
    headlineH6: CSSProperties;
    bodyB1Regular: CSSProperties;
    bodyB1Medium: CSSProperties;
    bodyB1Strong: CSSProperties;
    bodyB2Regular: CSSProperties;
    bodyB2Medium: CSSProperties;
    bodyB2Strong: CSSProperties;
    body1Bold: CSSProperties;
    body1Medium: CSSProperties;
    body2Bold: CSSProperties;
    body2Medium: CSSProperties;
    captionBold: CSSProperties;
    caption2: CSSProperties;
    caption2Bold: CSSProperties;
    captionC1Regular: CSSProperties;
    captionC1Strong: CSSProperties;
    captionC2Strong: CSSProperties;
    paragraphP1: CSSProperties;
    paragraphP2: CSSProperties;
    paragraphP3: CSSProperties;
    paragraphP3Strong: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    headlineH1: true;
    headlineH2: true;
    headlineH3: true;
    headlineH4: true;
    headlineH5: true;
    headlineH6: true;
    bodyB1Regular: true;
    bodyB1Medium: true;
    bodyB1Strong: true;
    bodyB2Regular: true;
    bodyB2Medium: true;
    bodyB2Strong: true;
    body1Bold: true;
    body1Medium: true;
    body2Bold: true;
    body2Medium: true;
    captionBold: true;
    caption2: true;
    caption2Bold: true;
    captionC1Regular: true;
    captionC1Strong: true;
    captionC2Strong: true;
    paragraphP1: true;
    paragraphP2: true;
    paragraphP3: true;
    paragraphP3Strong: true;
  }

  interface TypographyPropsColorOverrides {
    color: (theme: Theme) => string;
  }
}

export function getTypographyConfigs(
  fontFamilyName: string,
): ThemeOptions['typography'] {
  return {
    fontFamily: fontFamilyName,
    fontWeightRegular: fontWeight.regular,
    fontWeightMedium: fontWeight.medium,
    fontWeightSemiBold: fontWeight.semiBold,
    fontWeightBold: fontWeight.bold,
    allVariants: {
      lineHeight: 1.375,
    },
    headlineH1: {
      fontSize: '48px',
      fontWeight: fontWeight.bold,
      lineHeight: '64px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    headlineH2: {
      fontSize: '32px',
      fontWeight: fontWeight.bold,
      lineHeight: '44px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    headlineH3: {
      fontSize: '24px',
      fontWeight: fontWeight.bold,
      lineHeight: '32px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    headlineH4: {
      fontSize: '20px',
      fontWeight: fontWeight.bold,
      lineHeight: '28px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    headlineH5: {
      fontSize: '18px',
      fontWeight: fontWeight.semiBold,
      lineHeight: '22px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    headlineH6: {
      fontSize: '16px',
      fontWeight: fontWeight.semiBold,
      lineHeight: '20px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    h1: {
      fontSize: '3rem',
      fontWeight: fontWeight.bold,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: fontWeight.bold,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: fontWeight.bold,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: fontWeight.bold,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: fontWeight.semiBold,
    },
    bodyB1Regular: {
      fontSize: '16px',
      fontWeight: fontWeight.regular,
      lineHeight: '22px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    bodyB1Medium: {
      fontSize: '16px',
      fontWeight: fontWeight.medium,
      lineHeight: '22px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    bodyB1Strong: {
      fontSize: '16px',
      fontWeight: fontWeight.semiBold,
      lineHeight: '22px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    bodyB2Regular: {
      fontSize: '14px',
      fontWeight: fontWeight.regular,
      lineHeight: '18px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    bodyB2Medium: {
      fontSize: '14px',
      fontWeight: fontWeight.medium,
      lineHeight: '18px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    bodyB2Strong: {
      fontSize: '14px',
      fontWeight: fontWeight.semiBold,
      lineHeight: '18px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    body1: {
      fontSize: '1rem',
    },
    body1Medium: {
      fontSize: '1rem',
      fontWeight: fontWeight.medium,
    },
    body1Bold: {
      fontSize: '1rem',
      fontWeight: fontWeight.semiBold,
    },
    body2: {
      fontSize: '0.875rem',
    },
    body2Medium: {
      fontSize: '0.875rem',
      fontWeight: fontWeight.medium,
    },
    body2Bold: {
      fontSize: '0.875rem',
      fontWeight: fontWeight.semiBold,
    },
    caption: {
      fontSize: '0.75rem',
    },
    captionBold: {
      fontSize: '0.75rem',
      fontWeight: fontWeight.semiBold,
    },
    caption2: {
      fontSize: '0.625rem',
    },
    caption2Bold: {
      fontSize: '0.625rem',
      fontWeight: fontWeight.semiBold,
    },
    captionC1Regular: {
      fontSize: '0.75rem',
      fontWeight: fontWeight.regular,
      fontStyle: 'normal',
      lineHeight: '16px',
    },
    captionC1Strong: {
      fontSize: '0.75rem',
      fontWeight: fontWeight.semiBold,
      fontStyle: 'normal',
      lineHeight: '16px',
    },
    captionC2Strong: {
      fontSize: '10px',
      fontWeight: fontWeight.semiBold,
      fontStyle: 'normal',
      lineHeight: '14px',
      letterSpacing: '0px',
    },
    paragraphP1: {
      fontSize: '16px',
      fontWeight: fontWeight.regular,
      lineHeight: '24px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    paragraphP2: {
      fontSize: '14px',
      fontWeight: fontWeight.regular,
      lineHeight: '20px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    paragraphP3: {
      fontSize: '12px',
      fontWeight: fontWeight.regular,
      lineHeight: '18px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
    paragraphP3Strong: {
      fontSize: '12px',
      fontWeight: fontWeight.semiBold,
      lineHeight: '18px',
      fontStyle: 'normal',
      letterSpacing: '0px',
    },
  };
}
